/**
 * Checks if an error indicates a permission denied or unauthorized error.
 * These errors should not be retried as they require user action.
 */
export function isPermissionDeniedError(error: Error) {
  const message = String(error.message ?? '')
  // ConnectError stringifies its code as `[permission_denied] …`, which neither the
  // camelCase nor the `unauthorized` pattern matches — without it, every denied RPC
  // burns three backoff rounds before the UI can say the user has no access.
  return (
    /PermissionDenied/i.test(message) ||
    /\[permission_denied\]/i.test(message) ||
    /unauthorized/i.test(message)
  )
}

/**
 * Checks if an error indicates a not-found resource.
 * These errors should not be retried as the resource won't appear on retry.
 */
export function isNotFoundError(error: Error) {
  const message = String(error.message ?? '')
  return /\[not_found\]/i.test(message) || /NotFound/i.test(message)
}

export function isRateLimitedError(error: Error) {
  return /\bHTTP 429\b/i.test(String(error.message ?? ''))
}

/**
 * Default retry function for React Query.
 * - Does not retry permission denied errors (keeps UI responsive)
 * - Does not retry not-found errors (resource won't appear on retry)
 * - Does not retry rate-limited errors (the server provides a cooldown)
 * - Retries up to 3 times for other errors
 */
export function retry(failureCount: number, error: unknown) {
  if (error instanceof Error) {
    if (isPermissionDeniedError(error)) {
      return false
    }

    if (isNotFoundError(error)) {
      return false
    }

    if (isRateLimitedError(error)) {
      return false
    }
  }

  return failureCount < 3
}
